import { useQuery } from "react-query";
import { fetchAiReviews, fetchAiReviewCount, FetchAiReviewsParams } from "api/aiReviewService";

interface UseAIReviewListParams {
  tab: "pinned" | "recent";
  page: number;
  pageSize: number;
}

/**
 * 보고서 관리 페이지 전용 훅.
 * 탭(핀/최근)과 페이지 번호에 따라 목록과 총건수를 조회한다.
 * - recent 탭: pinnedOnly 미지정 + createdAt DESC 명시 (핀 여부 무관 최신순)
 * - pinned 탭: pinnedOnly=true → 백엔드가 pinnedAt DESC 반환
 * 탭 라벨에 각 건수를 항상 표시하기 위해 양쪽 count를 병렬로 조회한다.
 * staleTime은 전역 default(5분, index.tsx)를 활용.
 */
export const useAIReviewList = ({ tab, page, pageSize }: UseAIReviewListParams) => {
  const offset = (page - 1) * pageSize;
  const params: FetchAiReviewsParams = {
    limit: pageSize,
    offset,
    ...(tab === "pinned"
      ? { pinnedOnly: true }
      : { orderBy: "createdAt", orderDirection: "DESC" }),
  };

  const listQuery = useQuery({
    queryKey: ["aiReviewList", tab, page, pageSize],
    queryFn: async () => {
      const res = await fetchAiReviews(params);
      if (res.error) throw new Error(res.message);
      return res.data ?? [];
    },
    keepPreviousData: true,
  });

  const pinnedCountQuery = useQuery({
    queryKey: ["aiReviewCount", "pinned"],
    queryFn: async () => {
      const res = await fetchAiReviewCount({ pinnedOnly: true });
      if (res.error) throw new Error(res.message);
      return res.data?.total ?? 0;
    },
  });

  const recentCountQuery = useQuery({
    queryKey: ["aiReviewCount", "recent"],
    queryFn: async () => {
      const res = await fetchAiReviewCount({});
      if (res.error) throw new Error(res.message);
      return res.data?.total ?? 0;
    },
  });

  const countQuery = tab === "pinned" ? pinnedCountQuery : recentCountQuery;

  return {
    listQuery,
    countQuery,
    pinnedCount: pinnedCountQuery.data,
    recentCount: recentCountQuery.data,
  };
};
